import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';

const CANONICAL_INSTRUCTIONS_URL =
  'https://nexus.digitaldynamicsolution.com/data-deletion.html';

export default function UserDataDeletion() {
  useEffect(() => {
    document.title = 'User Data Deletion — Nexus';
  }, []);

  return (
    <Box
      sx={{
        maxWidth: 672,
        mx: 'auto',
        p: 3,
        pb: 6,
        color: 'text.primary',
        '& ul, & ol': { pl: 2.5 },
        '& li': { mb: 0.75 },
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        User Data Deletion
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Effective date:</strong> March 30, 2026 · <strong>Service:</strong> Nexus operated by
        Digital Dynamic Solution.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        Official page URL (HTTPS)
      </Typography>
      <Typography paragraph>
        This page is served only over <strong>HTTPS</strong>. For app developer settings (for example
        Meta’s <strong>User data deletion</strong> section), use this exact instructions URL:
      </Typography>
      <Box
        component="pre"
        sx={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: '0.85rem',
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          wordBreak: 'break-all',
          whiteSpace: 'pre-wrap',
        }}
      >
        {CANONICAL_INSTRUCTIONS_URL}
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        Do not use <code>http://</code> or a bare domain without <code>https://</code> — validation may
        fail.
      </Typography>
      <Typography paragraph>
        If Meta gave you a <strong>confirmation code</strong> after a data deletion request, check status
        here:{' '}
        <Link component={RouterLink} to="/data-deletion-status.html">
          Data deletion request status
        </Link>
        .
      </Typography>

      <Typography paragraph>
        This page explains how you can request deletion of personal data associated with your Nexus
        account, including data from <strong>Meta-connected Facebook Pages / Instagram Business</strong> and
        other platforms you linked. For how we handle data generally, see our{' '}
        <Link component={RouterLink} to="/privacy-policy.html">
          Privacy Policy
        </Link>
        .
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        What you can request
      </Typography>
      <Typography paragraph>
        You may request that we delete your account and personal data we hold about you in connection with
        Nexus, subject to limited exceptions (for example where we must retain certain information to
        comply with law or resolve disputes).
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        How to request deletion
      </Typography>
      <Box component="ol" sx={{ pl: 2.5 }}>
        <Typography component="li" paragraph>
          <strong>Email (recommended):</strong> Send a request from the email address associated with your
          account to{' '}
          <Link href="mailto:luthdigitalconsult@gmail.com">luthdigitalconsult@gmail.com</Link> with the
          subject line <strong>“Data deletion request”</strong>. Include your full name and the email you
          use to log in.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Self-service in the app:</strong> We may add account deletion or data export in Settings in
          a future release. <strong>Until then, email is the supported way</strong> to request deletion unless
          your administrator documents otherwise.
        </Typography>
      </Box>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        What we delete
      </Typography>
      <Typography paragraph>
        After we verify your identity (for example by confirming you control the email on the account), we
        remove or irreversibly anonymize personal data associated with your Nexus user, subject to legal
        exceptions (such as information we must keep to comply with law or establish legal claims).
      </Typography>
      <Typography paragraph>
        <strong>Categories of data removed or anonymized</strong> typically include:
      </Typography>
      <Box component="ul">
        <Typography component="li" paragraph>
          <strong>Account credentials and profile:</strong> email, username, password hash, name,
          company, phone, and other profile fields stored for your login.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Clients and workspace data you own:</strong> client records, brand and scheduling
          preferences, and Facebook Page assignments and page tokens stored in Nexus for those clients.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Connected social accounts:</strong> OAuth and API tokens and related metadata for platforms
          you connected (for example Meta for Facebook Pages and Instagram Business, LinkedIn, and similar),
          including entries in our platform-connection records.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Optional manual Instagram credentials:</strong> if you used optional manual Instagram
          connection, any encrypted Instagram password and related manual-connection settings we stored for
          your account.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Content and activity in Nexus:</strong> drafts, posts, scheduled posts, analytics rows
          tied to your accounts, campaigns, and similar user-created content stored in our application
          database for your user.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Media you uploaded</strong> to Nexus (for example images or files attached to posts or the
          media library), where stored on our systems.
        </Typography>
        <Typography component="li" paragraph>
          <strong>Support or account-related correspondence</strong> retained only for your service
          relationship, where applicable.
        </Typography>
      </Box>
      <Typography paragraph>
        <strong>What we may retain:</strong> aggregated or de-identified statistics that cannot reasonably be
        linked back to you; backups for a limited period until they roll off; and information we are
        required or permitted to keep under applicable law.
      </Typography>
      <Typography paragraph>
        <strong>Third-party platforms:</strong> Data you already published on Facebook, Instagram, or other
        networks may remain visible there under their rules. Removing Nexus does not delete those public posts
        by itself — use each platform’s tools or your deletion request with us plus those services as
        needed.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        Timing
      </Typography>
      <Typography paragraph>
        We aim to confirm receipt within a reasonable period and complete deletion within the timeframe
        required by applicable law (often within 30–45 days, depending on jurisdiction and complexity), unless
        a shorter period applies.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        Facebook / Meta
      </Typography>
      <Typography paragraph>
        If you connected Nexus to Meta for Facebook Pages or Instagram Business, you can also review
        Facebook’s settings for apps and websites. Removing or limiting Nexus there may stop future access
        but may not delete data we already stored; use the deletion request above for data held in Nexus.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        Contact
      </Typography>
      <Typography>
        <Link href="mailto:luthdigitalconsult@gmail.com">luthdigitalconsult@gmail.com</Link>
      </Typography>
    </Box>
  );
}
